import React from 'react';
import { WifiOff, CloudOff, RefreshCw } from 'lucide-react';
import './ReconnectNotice.css';

/**
 * WT-1 (16 Oct walk-through): the calm "can't reach Marakin" notice.
 * AuthProvider renders it while a stored session's /auth/me keeps failing
 * for a reason that isn't the session itself (no signal, a captive portal, a
 * 429 from the shared limit, a 5xx), or when the first try is taking a long
 * time on one bar of signal. The token is kept and retried; this tells the
 * person why the app is waiting, and goes the moment a try succeeds.
 *
 * `state`: null (hidden) or { checking, reason, canSignOut }.
 *   reason 'offline' — no answer (or a captive portal): "check your signal"
 *   reason 'busy'    — 429 / 5xx: the server, not their signal
 *   reason 'slow'    — the first try is still out after a few seconds
 * After a few failures it also offers "Sign out" (review: nobody may be stuck
 * behind it, e.g. someone else wanting to sign in on a shared device).
 *
 * The role="status" region is always mounted, empty when idle, so screen
 * readers announce the notice when it appears. The buttons sit in an
 * aria-live="off" group: their visible labels ("Try again" / "Trying…") are
 * their accessible names, and a retry every few seconds isn't re-announced.
 */
const COPY = {
  offline: { title: "Can't reach Marakin right now.", body: 'Retrying… check your signal.' },
  busy: { title: 'Marakin is busy right now.', body: 'Retrying…' },
  slow: { title: 'Taking a while to reach Marakin.', body: 'Still trying… check your signal.' },
};

const ReconnectNotice = ({ state, onRetry, onSignOut }) => {
  const checking = !!state?.checking;
  const copy = COPY[state?.reason] || COPY.offline;
  const Icon = state?.reason === 'busy' ? CloudOff : WifiOff;
  return (
    <div role="status" className="reconnect-notice-region">
      {state && (
        <div className="reconnect-notice">
          <Icon size={20} aria-hidden="true" className="reconnect-notice-icon" />
          <p className="reconnect-notice-text">
            <strong>{copy.title}</strong> {copy.body}
          </p>
          <div className="reconnect-notice-actions" aria-live="off">
            <button
              type="button"
              className="reconnect-notice-btn"
              aria-disabled={checking}
              onClick={() => { if (!checking) onRetry(); }}
            >
              <RefreshCw size={16} aria-hidden="true" className={checking ? 'reconnect-notice-spin' : undefined} />
              {checking ? 'Trying…' : 'Try again'}
            </button>
            {state.canSignOut && (
              <button type="button" className="reconnect-notice-signout" onClick={onSignOut}>
                Sign out
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReconnectNotice;
